/**
 * Bridge Connectivity Hook
 *
 * Provides reactive bridge connectivity state for the self-service UI.
 * Continuously monitors the connection to the DocCirrus bridge and exposes
 * status to components that need to adapt based on connectivity.
 *
 * @since 1.0.0
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  formatUtcInstant,
  systemClock,
} from "@ascleo/types/date-time-validity";
import { docCirrusRpcClient } from "./doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../diagnostic-logger";
import { reportOperationalError } from "../operational-error-telemetry";

/**
 * Bridge connectivity state
 */
export interface BridgeConnectivityState {
  /** Whether the bridge is currently connected */
  isConnected: boolean;
  /** Timestamp of the last successful connectivity check */
  lastChecked: Date | null;
  /** Error message if the last check failed */
  error: string | null;
  /** Whether a connectivity check is currently in progress */
  isChecking: boolean;
}

interface UseBridgeConnectivityOptions {
  /** Polling interval in milliseconds. Default: 30000 (30 seconds) */
  pollInterval?: number;
  /** Whether connectivity monitoring is enabled. Default: true */
  enabled?: boolean;
  /** Number of consecutive failures before marking as disconnected. Default: 3 */
  failureThreshold?: number;
}

const DEFAULT_POLL_INTERVAL = 30000;
const DEFAULT_FAILURE_THRESHOLD = 3;

/**
 * Hook for continuous bridge connectivity monitoring.
 *
 * Uses polling to check bridge connectivity status at regular intervals.
 * Implements failure counting to avoid false negatives from temporary network blips.
 *
 * @param options - Configuration options
 * @returns Current bridge connectivity state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConnected, error } = useBridgeConnectivity()
 *
 *   if (!isConnected) {
 *     return <OfflineBanner />
 *   }
 *
 *   return <NormalContent />
 * }
 * ```
 */
export function useBridgeConnectivity(
  options: UseBridgeConnectivityOptions = {},
): BridgeConnectivityState {
  const {
    pollInterval = DEFAULT_POLL_INTERVAL,
    enabled = true,
    failureThreshold = DEFAULT_FAILURE_THRESHOLD,
  } = options;

  const [state, setState] = useState<BridgeConnectivityState>({
    isConnected: true, // Assume connected until proven otherwise
    lastChecked: null,
    error: null,
    isChecking: true, // Start with checking state
  });

  // Refs for stable values across renders
  const failureCountRef = useRef(0);
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const hasEverConnectedRef = useRef(false);
  const lastConnectionStateRef = useRef<boolean | null>(null);

  // Perform a single connectivity check
  const checkConnectivity = useCallback(async () => {
    if (!isMountedRef.current) return;

    setState((prev) => ({ ...prev, isChecking: true }));

    try {
      const status = await docCirrusRpcClient.connectivity.status();

      if (isMountedRef.current) {
        const checkedAt = systemClock.now();
        const checkedAtInstant = formatUtcInstant(checkedAt);
        if (status.isConnectedToAPI) {
          if (lastConnectionStateRef.current === false) {
            frontdeskDiagnosticLogger.info(
              "[FrontDesk+] Bridge connectivity restored",
              {
                checkedAt: checkedAtInstant,
              },
            );
          }
        } else if (lastConnectionStateRef.current !== false) {
          frontdeskDiagnosticLogger.warn(
            "[FrontDesk+] Bridge connectivity check reported the upstream API as unavailable",
            {
              checkedAt: checkedAtInstant,
              status,
            },
          );
          void reportOperationalError({
            action: "bridge.upstream-unavailable",
            severity: "warning",
            source: "lifecycle",
            transport: "effect-rpc-http",
            component: "useBridgeConnectivity",
            context: {
              upstreamConnected: false,
            },
          });
        }
        lastConnectionStateRef.current = status.isConnectedToAPI;

        // Reset failure counter on success
        failureCountRef.current = 0;
        hasEverConnectedRef.current = true;

        setState({
          isConnected: status.isConnectedToAPI,
          lastChecked: checkedAt,
          error: null,
          isChecking: false,
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        // Track consecutive failures
        failureCountRef.current++;

        const errorMessage =
          error instanceof Error ? error.message : "Connection check failed";

        // Mark as disconnected immediately if:
        // 1. We've never successfully connected (initial check failed), OR
        // 2. We've reached the failure threshold (connection lost after being connected)
        const shouldMarkDisconnected =
          !hasEverConnectedRef.current ||
          failureCountRef.current >= failureThreshold;

        if (shouldMarkDisconnected) {
          if (lastConnectionStateRef.current !== false) {
            frontdeskDiagnosticLogger.error(
              "[FrontDesk+] Bridge connectivity check failed and the client is now considered offline",
              {
                error: errorMessage,
                failureCount: failureCountRef.current,
                failureThreshold,
              },
            );
            void reportOperationalError({
              action: "bridge.connectivity-offline",
              severity: "error",
              source: "lifecycle",
              transport: "effect-rpc-http",
              component: "useBridgeConnectivity",
              error,
              context: {
                consecutiveFailures: failureCountRef.current,
                failureThreshold,
                hadPreviousConnection: hasEverConnectedRef.current,
              },
            });
          }
          lastConnectionStateRef.current = false;
          setState({
            isConnected: false,
            lastChecked: systemClock.now(),
            error: errorMessage,
            isChecking: false,
          });
        } else {
          // Keep previous connected state, just update error
          setState((prev) => ({
            ...prev,
            error: errorMessage,
            isChecking: false,
          }));
        }
      }
    }
  }, [failureThreshold]);

  // Set up polling
  useEffect(
    function initial() {
      if (!enabled) {
        return;
      }

      isMountedRef.current = true;

      // Perform initial check immediately
      checkConnectivity();

      // Start polling
      pollIntervalRef.current = setInterval(checkConnectivity, pollInterval);

      // Cleanup on unmount or when options change
      return function clearIntervalTimer() {
        isMountedRef.current = false;
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    },
    [enabled, pollInterval, checkConnectivity],
  );

  return state;
}
